/**
 * Grading Reviewer Component
 *
 * Displays grading results and allows human overrides.
 */

import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Divider,
  Slider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Collapse,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as CheckIcon,
  Cancel as NoIcon,
  ThumbUp as ConfirmIcon,   // 新增
} from "@mui/icons-material";

interface RubricPointEvidence {
  rubricPointId: string;
  rubricPointDescription: string;
  awarded: boolean;
  marksAwarded: number;
  maxMarks: number;
  evidenceQuote?: string;
  reasoning: string;
}

interface GradingDraft {
  id: string;
  question_id: string;
  marks_awarded: number;
  max_marks: number;
  confidence: number;
  rationale: string;
  student_feedback: string;
  version: number;
}

// ===== 新增 review flag type =====
interface ReviewFlag {
  type: "verification" | "low_grading_confidence" | "low_transcript_confidence" | "needs_review";
  label: string;
  severity: "error" | "warning" | "info";
}
// ===== 新增結束 =====

interface GradingReviewerProps {
  gradingDraft?: GradingDraft;
  transcript?: string;
  onOverride: (
    marks: number,
    feedback: string,
    reason: string
  ) => Promise<void>;
  saving?: boolean;
  flags?: ReviewFlag[];       // 新增
  isConfirmed?: boolean;      // 新增
  onConfirm?: () => void;     // 新增
}

export function GradingReviewer({
  gradingDraft,
  transcript,
  onOverride,
  saving = false,
  flags = [],          // 新增
  isConfirmed = false, // 新增
  onConfirm,           // 新增
}: GradingReviewerProps) {
  const hasFlags = flags.length > 0;  // 新增
  const [editing, setEditing] = useState(false);
  const [newMarks, setNewMarks] = useState(0);
  const [newFeedback, setNewFeedback] = useState("");
  const [reason, setReason] = useState("");
  const [showRationale, setShowRationale] = useState(false);

  useEffect(() => {
    if (gradingDraft) {
      setNewMarks(gradingDraft.marks_awarded);
      setNewFeedback(gradingDraft.student_feedback);
    }
  }, [gradingDraft]);

  if (!gradingDraft) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">沒有找到評分結果</Typography>
      </Paper>
    );
  }

  // Parse rationale
  let rationaleItems: RubricPointEvidence[] = [];
  try {
    rationaleItems = JSON.parse(gradingDraft.rationale);
  } catch {
    // Rationale might be a string
  }

  const handleStartEdit = () => {
    setEditing(true);
    setNewMarks(gradingDraft.marks_awarded);
    setNewFeedback(gradingDraft.student_feedback);
    setReason("");
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      return; // Require reason
    }
    await onOverride(newMarks, newFeedback, reason);
    setEditing(false);
  };

  const percentScore = Math.round(
    (gradingDraft.marks_awarded / gradingDraft.max_marks) * 100
  );

  return (
    <Paper sx={{ p: 3 }}>
      {/* Score overview */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4" color="primary.main">
            {gradingDraft.marks_awarded}
          </Typography>
          <Typography variant="h5" color="text.secondary">
            / {gradingDraft.max_marks}
          </Typography>
          <Chip
            label={`${percentScore}%`}
            color={
              percentScore >= 70
                ? "success"
                : percentScore >= 50
                ? "warning"
                : "error"
            }
          />
          <Chip
            label={`信心度: ${Math.round(gradingDraft.confidence * 100)}%`}
            variant="outlined"
            size="small"
            color={                                          // 新增
              gradingDraft.confidence < 0.5                  // 新增
                ? "error"                                    // 新增
                : gradingDraft.confidence < 0.7              // 新增
                ? "warning"                                  // 新增
                : "default"                                  // 新增
            }        
          />
          <Box sx={{ flexGrow: 1 }} />

          {/* ===== 新增確認自動評分無誤button ===== */}
          {hasFlags && !isConfirmed && !editing && onConfirm && (
            <Button
              startIcon={<ConfirmIcon />}
              onClick={onConfirm}
              variant="outlined"
              color="success"
            >
              確認無誤
            </Button>
          )}

          {isConfirmed && (
            <Chip
              icon={<CheckIcon />}
              label="已確認"
              color="success"
              variant="outlined"
            />
          )}
          {/* ===== 新增結束 ===== */}

          {!editing && (
            <Button
              startIcon={<EditIcon />}
              onClick={handleStartEdit}
              variant="outlined"
            >
              修改評分
            </Button>
          )}
        </Stack>
      </Box>

      {/* Editing form */}
      {editing && (
        <Card sx={{ mb: 3, bgcolor: "warning.light" }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              修改評分
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                分數: {newMarks} / {gradingDraft.max_marks}
              </Typography>
              <Slider
                value={newMarks}
                onChange={(_, v) => setNewMarks(v as number)}
                min={0}
                max={gradingDraft.max_marks}
                step={0.5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="學生回饋"
              value={newFeedback}
              onChange={(e) => setNewFeedback(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              required
              label="修改原因 (必填)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="請說明修改原因..."
              sx={{ mb: 2 }}
              error={editing && !reason.trim()}
              helperText={editing && !reason.trim() ? "請輸入修改原因" : ""}
            />

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={saving || !reason.trim()}
              >
                保存修改
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving}
              >
                取消
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Student feedback */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          學生回饋
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            fontFamily: '"Noto Sans TC", sans-serif',
          }}
        >
          {gradingDraft.student_feedback || (
            <Typography color="text.secondary" fontStyle="italic">
              (無回饋)
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Rationale */}
      <Box>
        <Button
          onClick={() => setShowRationale(!showRationale)}
          endIcon={showRationale ? <CollapseIcon /> : <ExpandIcon />}
        >
          評分理據
        </Button>

        <Collapse in={showRationale || (hasFlags && !isConfirmed)}>
          <Box sx={{ mt: 2 }}>
            {Array.isArray(rationaleItems) && rationaleItems.length > 0 ? (
              rationaleItems.map((item, index) => (
                <Card
                  key={item.rubricPointId || index}
                  sx={{
                    mb: 2,
                    border: 1,
                    borderColor: item.awarded ? "success.light" : "grey.300",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      {item.awarded ? (
                        <CheckIcon color="success" />
                      ) : (
                        <NoIcon color="disabled" />
                      )}
                      <Typography variant="subtitle2">
                        {item.rubricPointDescription}
                      </Typography>
                      <Chip
                        label={`${item.marksAwarded} / ${item.maxMarks}`}
                        size="small"
                        color={item.awarded ? "success" : "default"}
                      />
                    </Stack>

                    {item.evidenceQuote && (
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: "grey.100",
                          borderLeft: 3,
                          borderColor: "primary.main",
                          mb: 1,
                          fontStyle: "italic",
                        }}
                      >
                        "{item.evidenceQuote}"
                      </Box>
                    )}

                    <Typography variant="body2" color="text.secondary">
                      {item.reasoning}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Alert severity="info">
                {typeof gradingDraft.rationale === "string"
                  ? gradingDraft.rationale
                  : "沒有詳細評分理據"}
              </Alert>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Original transcript reference */}
      {transcript && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              學生答案參考
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                maxHeight: 150,
                overflow: "auto",
                fontSize: "0.9rem",
                fontFamily: '"Noto Serif TC", serif',
              }}
            >
              {transcript}
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
}
